import { useAlert } from '@inbody/core';
import { useMutation, useQuery } from '@tanstack/react-query';

import { v2QueryKeys } from './queryKeys';
import { BulkDownloadRequestBody, BulkDownloadStatus } from './schemas';
import { useV2FileApis } from './useV2FileApis';

import { isShownByGlobalToast } from '@/pages/v2/lib';

/** 진행 중 상태에서만 2초 간격 폴링, 종료 상태(완료/실패/빈 결과)면 중단 */
const POLL_INTERVAL_MS = 2_000;

const isTerminal = (status?: BulkDownloadStatus) =>
  status === 'Completed' || status === 'Failed' || status === 'Empty';

/** N13 — 선택 항목 비동기 ZIP 요청 → jobId (요청만, 진행 추적은 useDownloadJob) */
export const useRequestBulkDownload = () => {
  const { postBulkDownloadAPI } = useV2FileApis();
  const { toastAlert } = useAlert();

  return useMutation({
    mutationFn: (body: BulkDownloadRequestBody) => postBulkDownloadAPI(body),
    onSuccess: (data) => {
      // 권한 제외(skippedCount) 안내는 작업 완료 토스트에 합쳐 한 번에 보여준다(CART-07)
      // — 여기서 따로 띄우면 곧바로 'ZIP 다운로드를 시작합니다.'에 가려진다
      if (data.status === 'Empty') {
        toastAlert.warning(
          data.skippedCount > 0
            ? `다운로드할 수 있는 항목이 없습니다. 권한이 없는 ${data.skippedCount}개 항목은 제외했습니다.`
            : '다운로드할 수 있는 항목이 없습니다.',
        );
      }
    },
    onError: (error) => {
      if (!isShownByGlobalToast(error))
        toastAlert.error('다운로드 요청에 실패했습니다.');
    },
  });
};

/**
 * N13/N15 공용 — ZIP 작업 상태 폴링.
 * jobId가 생기면 종료 상태까지 폴링하고, Completed 응답의 url로 내려받는다.
 * (url·fileName은 Completed일 때만 · expiresAt 지나면 만료 — Hangfire 배치가 정리)
 *
 * 사용: const { data: job } = useDownloadJob(jobId);  // jobId=null이면 대기
 */
export const useDownloadJob = (jobId: string | null) => {
  const { getBulkDownloadJobAPI } = useV2FileApis();

  return useQuery({
    queryKey: v2QueryKeys.downloadJob(jobId ?? 'idle'),
    queryFn: () => getBulkDownloadJobAPI(jobId as string),
    enabled: !!jobId,
    refetchInterval: (query) =>
      isTerminal(query.state.data?.status) ? false : POLL_INTERVAL_MS,
  });
};
